import {ChangeEvent, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import CollectionView from '../components/CollectionView';
import Modal from '../components/Modal';
import {Collection} from '../models/collection';
import ApiKeyService from '../services/apiKeyService';
import CollectionService from '../services/collectionServise';
import {formatDate} from '../util';

import './CSS/Collection.css';

const Collections: React.FC = () => {
    const [collections, setCollections] = useState<Collection[]>([]);

    const navigate = useNavigate();

    const collectionServise = new CollectionService();
    const apiKeyService = new ApiKeyService();

    const getCollections = () => {
        const getCollectionsPromise = collectionServise.get();

        getCollectionsPromise
            .then((collections) => {
                if (collections !== undefined) {
                    setCollections(collections);
                }
            })
            .catch((reson) => {
                console.error(reson);
            });
    };

    const getApiKey = () => {
        const getApiKeyPromise = apiKeyService.getApiKey();

        getApiKeyPromise
            .then((result) => {
                if (result !== null && result.post_key !== undefined) {
                    setPostKey(result.post_key);
                }
            })
            .catch((reson) => {
                console.error(reson);
            });
    };

    useEffect(() => {
        getCollections();
        getApiKey();
    }, []);

    /**
     * コレクションの削除を行います。
     * @param collection 削除するコレクションを指定します。
     */
    const onClickRowDeleteButton = (collection: Collection) => {
        setDeleteErrorMessage('');
        setDeleteModalCollectionName(collection.collection_name ?? '');
        setDeleteModalIsOpen(true);
        setDeleteTargetCollection(collection);
    };

    /**
     * コレクションの編集を行います。
     * @param collection 編集するコレクションを指定します。
     */
    const onClickRowEditButton = (collection: Collection) => {
        setEditModalCollectionName(collection.collection_name ?? '');
        setEditModalIsOpen(true);
    };

    /**
     * コレクションにデータの追加を行います。
     * @param collection データを追加するコレクションを指定します。
     */
    const onClickRowAddDataButton = (collection: Collection) => {
        setAddDataErrorMessage('');
        setAddDataModalIsOpen(true);
        setAddDataTargetCollection(collection);
    };

    /**
     * コレクション作成モーダル
     */

    const [createModalIsOpen, setCreateModalIsOpen] = useState(false);
    const [createCollectionName, setAddCollectionName] = useState('');
    const [createCollectionTimeZone, setCreateCollectionTimeZone] = useState('+09:00');
    const [errorMessage, setErrorMessage] = useState('');

    const onClickCreateButton = () => {
        setCreateModalIsOpen(true);
    };

    const onChangeCreateCollectionName = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setAddCollectionName(value);
    };

    const onChangeTimeZone = (event: ChangeEvent<HTMLSelectElement>) => {
        const value = event.currentTarget.value;
        setCreateCollectionTimeZone(value);
    };

    const onClickCreateCollectionButton = () => {
        setErrorMessage('');
        const createPromise = collectionServise.create(createCollectionName, createCollectionTimeZone);

        createPromise
            .then((result) => {
                console.log(result);
                getCollections();
                setAddCollectionName('');
                setCreateCollectionTimeZone('+09:00');
                setErrorMessage('');
                setCreateModalIsOpen(false);
            })
            .catch((reson) => {
                console.error(reson);
                setErrorMessage('コレクション作成に失敗しました。' + reson.message);
            });
    };

    const onClickCreateCancelButton = () => {
        setAddCollectionName('');
        setCreateCollectionTimeZone('+09:00');
        setErrorMessage('');
        setCreateModalIsOpen(false);
    };

    /**
     * コレクション編集モーダル
     */

    const [editModalIsOpen, setEditModalIsOpen] = useState(false);
    const [editModalCollectionName, setEditModalCollectionName] = useState('');

    const onClickEditCancelButton = () => {
        setEditModalIsOpen(false);
        setEditModalCollectionName('');
    };

    /**
     * コレクション削除モーダル
     */

    const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
    const [deleteErrorMessage, setDeleteErrorMessage] = useState('');
    const [deleteModalCollectionName, setDeleteModalCollectionName] = useState('');
    const [deleteTargetCollection, setDeleteTargetCollection] = useState<Collection>({});

    const onClickDeleteButton = () => {
        if (deleteTargetCollection.collection_id !== undefined) {
            const deletePromise = collectionServise.delete(deleteTargetCollection.collection_id);

            deletePromise
                .then((result) => {
                    console.log(result);
                    setDeleteModalIsOpen(false);
                    setDeleteModalCollectionName('');
                    getCollections();
                })
                .catch((reson) => {
                    setDeleteErrorMessage('コレクションの削除に失敗しました。' + reson.message);
                    console.error(reson);
                });
        }
    };

    const onClickDeleteCancelButton = () => {
        setDeleteModalIsOpen(false);
    };

    /**
     * データ追加モーダル
     */

    const initData = () => {
        const nowDateTime = new Date();
        const dateTimeString = formatDate(nowDateTime, 'yyyy-MM-ddTHH:mm:ssZ');
        const defaultData = '{"created_at": "' + dateTimeString + '"}';

        return defaultData;
    };

    const [addDataModalIsOpen, setAddDataModalIsOpen] = useState(false);
    const [addDataTargetCollection, setAddDataTargetCollection] = useState<Collection>({});
    const [addDataErrorMessage, setAddDataErrorMessage] = useState('');
    const [postKey, setPostKey] = useState('');
    const [addData, setAddData] = useState(initData());

    const onClickAdddDataModalCancelButton = () => {
        setAddDataModalIsOpen(false);
    };

    const onClcikAddDataButton = () => {
        if (addDataTargetCollection.collection_id !== undefined) {
            setAddDataErrorMessage('');
            const addDataPromise = collectionServise.addData(addDataTargetCollection.collection_id, postKey, addData);
            addDataPromise
                .then(() => {
                    setAddDataTargetCollection({});
                    setAddData(initData());
                    setAddDataModalIsOpen(false);
                })
                .catch((reson) => {
                    setAddDataErrorMessage('データの追加に失敗しました。 ' + reson.message);
                });
        }
    };

    const onChangePostKey = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setPostKey(value);
    };

    const onChangeAddData = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.currentTarget.value;
        setAddData(value);
    };

    const onClickRowReadDataButton = (collection: Collection) => {
        if (collection.collection_id !== undefined) {
            const to = `/dataView/${collection.collection_id}`;
            navigate(to);
        }
    };

    return (
        <div id='CollectionsPage' className='page'>
            <h2>コレクション</h2>
            <div>
                <button onClick={onClickCreateButton}>コレクション作成</button>
            </div>
            <div>
                <h3>コレクション一覧</h3>
                <div>
                    {collections.map((collection, index) => {
                        return (
                            <CollectionView
                                collection={collection}
                                onClickDeleteButton={onClickRowDeleteButton}
                                onClickEditButton={onClickRowEditButton}
                                onClickAddDataButton={onClickRowAddDataButton}
                                onClickReadDataButton={onClickRowReadDataButton}
                                key={index}
                            ></CollectionView>
                        );
                    })}
                </div>
            </div>
            <Modal isOpen={createModalIsOpen}>
                <h2>コレクション作成</h2>
                <div className='input-group'>
                    <label htmlFor='collection-name'>コレクション名</label>
                    <input id='collection-name' type='text' value={createCollectionName} onChange={onChangeCreateCollectionName}></input>
                </div>
                <div className='input-group'>
                    <label htmlFor='collection-timezone'>タイムゾーン</label>
                    <select id='collection-timezone' onChange={onChangeTimeZone} defaultValue='+09:00'>
                        <option value='-12:00'>UTC -12:00</option>
                        <option value='-11:00'>UTC -11:00</option>
                        <option value='-10:00'>UTC -10:00</option>
                        <option value='-09:30'>UTC -09:30</option>
                        <option value='-09:00'>UTC -09:00</option>
                        <option value='-08:00'>UTC -08:00</option>
                        <option value='-07:00'>UTC -07:00</option>
                        <option value='-06:00'>UTC -06:00</option>
                        <option value='-05:00'>UTC -05:00</option>
                        <option value='-04:00'>UTC -04:00</option>
                        <option value='-03:30'>UTC -03:30</option>
                        <option value='-03:00'>UTC -03:00</option>
                        <option value='-02:00'>UTC -02:00</option>
                        <option value='-01:00'>UTC -01:00</option>
                        <option value='00:00'>UTC 00:00</option>
                        <option value='+01:00'>UTC +01:00</option>
                        <option value='+02:00'>UTC +02:00</option>
                        <option value='+03:00'>UTC +03:00</option>
                        <option value='+03:30'>UTC +03:30</option>
                        <option value='+04:00'>UTC +04:00</option>
                        <option value='+04:30'>UTC +04:30</option>
                        <option value='+05:00'>UTC +05:00</option>
                        <option value='+05:30'>UTC +05:30</option>
                        <option value='+05:45'>UTC +05:45</option>
                        <option value='+06:00'>UTC +06:00</option>
                        <option value='+06:30'>UTC +06:30</option>
                        <option value='+07:00'>UTC +07:00</option>
                        <option value='+08:00'>UTC +08:00</option>
                        <option value='+08:45'>UTC +08:45</option>
                        <option value='+09:00'>UTC +09:00</option>
                        <option value='+09:30'>UTC +09:30</option>
                        <option value='+10:00'>UTC +10:00</option>
                        <option value='+10:30'>UTC +10:30</option>
                        <option value='+11:00'>UTC +11:00</option>
                        <option value='+12:00'>UTC +12:00</option>
                        <option value='+12:45'>UTC +12:45</option>
                        <option value='+13:00'>UTC +13:00</option>
                        <option value='+14:00'>UTC +14:00</option>
                    </select>
                </div>
                <div className='error'>{errorMessage}</div>
                <div className='modal-control'>
                    <button onClick={onClickCreateCollectionButton}>追加</button>
                    <button onClick={onClickCreateCancelButton} className='danger'>
                        キャンセル
                    </button>
                </div>
            </Modal>
            <Modal isOpen={editModalIsOpen}>
                <h2>コレクション編集</h2>
                <h3>{editModalCollectionName}</h3>
                <div className='modal-control'>
                    <button onClick={onClickEditCancelButton}>キャンセル</button>
                </div>
            </Modal>
            <Modal isOpen={deleteModalIsOpen}>
                <h2>削除しますか？</h2>
                <h3>{deleteModalCollectionName}</h3>
                <div className='modal-control'>
                    <div className='error'>{deleteErrorMessage}</div>
                    <button className='danger' onClick={onClickDeleteButton}>
                        削除
                    </button>
                    <button onClick={onClickDeleteCancelButton}>キャンセル</button>
                </div>
            </Modal>
            <Modal isOpen={addDataModalIsOpen}>
                <h3>データ追加</h3>
                <div className='input-group'>
                    <label>POST key</label>
                    <input type='text' value={postKey} onChange={onChangePostKey}></input>
                </div>
                <div className='input-group'>
                    <label>データ</label>
                    <textarea onChange={onChangeAddData} value={addData}>
                        {addData}
                    </textarea>
                </div>
                <div className='error'>{addDataErrorMessage}</div>
                <div className='modal-control'>
                    <button onClick={onClcikAddDataButton}>追加</button>
                    <button className='danger' onClick={onClickAdddDataModalCancelButton}>
                        キャンセル
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default Collections;
